import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/layout/SiteLayout";
import Seo from "@/components/base/Seo";
import type { Business } from "@/types/directory";
import { getFeaturedBusinesses } from "@/services/directoryService";
import BusinessGrid from "@/components/feature/BusinessGrid";
import LetterFilterBar from "@/pages/featured-listings/components/LetterFilterBar";

const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

export default function FeaturedListingsPage() {
  const [listings, setListings] = useState<Business[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [activeLetter, setActiveLetter] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(false);
    getFeaturedBusinesses(200)
      .then((items) => {
        if (mounted) setListings(items);
      })
      .catch(() => {
        if (mounted) {
          setListings([]);
          setError(true);
        }
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const sorted = useMemo(
    () => [...listings].sort((a, b) => a.name.localeCompare(b.name)),
    [listings],
  );

  const groups = useMemo(
    () =>
      ALPHABET.map((letter) => ({
        letter,
        count: sorted.filter((b) => b.name.trim().toUpperCase().startsWith(letter)).length,
      })),
    [sorted],
  );

  const activeLetterCount = useMemo(
    () => groups.filter((group) => group.count > 0).length,
    [groups],
  );

  const categoriesCovered = useMemo(
    () => new Set(sorted.map((b) => b.categorySlug)).size,
    [sorted],
  );

  const filtered = useMemo(
    () =>
      activeLetter
        ? sorted.filter((b) => b.name.trim().toUpperCase().startsWith(activeLetter))
        : sorted,
    [sorted, activeLetter],
  );

  return (
    <SiteLayout>
      <Seo
        title="Featured Listings A to Z — Coastal Bizz Connect"
        description="Browse every featured and sponsored business listing on Coastal Bizz Connect in alphabetical order — from jewellery showrooms and sweets shops to hospitals, hotels and builders across coastal Karnataka."
        keywords="featured business listings, A to Z listings Karnataka, sponsored listings Mangaluru, verified businesses Udupi, top businesses North Canara"
        path="/featured-listings"
      />

      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1400px] px-6 py-10 md:py-12">
          <nav className="flex flex-wrap items-center gap-2 text-xs text-foreground-500" aria-label="Breadcrumb">
            <Link to="/" className="cursor-pointer transition-colors hover:text-primary-700">
              Home
            </Link>
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-arrow-right-s-line text-sm" />
            </span>
            <Link to="/directory" className="cursor-pointer transition-colors hover:text-primary-700">
              All Listings
            </Link>
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-arrow-right-s-line text-sm" />
            </span>
            <span className="font-medium text-foreground-700">Featured Listings A to Z</span>
          </nav>

          <div className="mt-3 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1 text-xs font-semibold text-accent-900">
                <span className="flex h-3.5 w-3.5 items-center justify-center">
                  <i className="ri-star-smile-line text-sm" />
                </span>
                Handpicked &amp; verified
              </span>
              <h1 className="mt-3 font-heading text-2xl font-extrabold text-foreground-950 md:text-4xl">
                Featured listings A to Z
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
                Every featured and sponsored business on Coastal Bizz Connect, arranged
                alphabetically. Pick a letter to jump straight to listings that start with it, then
                open any business to see full details, timings and contact options.
              </p>
            </div>

            <div className="flex shrink-0 gap-3">
              <div className="rounded-lg border border-background-200 bg-background-50 px-5 py-3">
                <p className="font-heading text-xl font-extrabold text-primary-700">{sorted.length}</p>
                <p className="mt-0.5 text-xs font-medium text-foreground-500">Featured listings</p>
              </div>
              <div className="rounded-lg border border-background-200 bg-background-50 px-5 py-3">
                <p className="font-heading text-xl font-extrabold text-accent-600">{categoriesCovered}</p>
                <p className="mt-0.5 text-xs font-medium text-foreground-500">Categories</p>
              </div>
              <div className="rounded-lg border border-background-200 bg-background-50 px-5 py-3">
                <p className="font-heading text-xl font-extrabold text-secondary-700">{activeLetterCount}</p>
                <p className="mt-0.5 text-xs font-medium text-foreground-500">Letters</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <LetterFilterBar
        groups={groups}
        activeLetter={activeLetter}
        total={sorted.length}
        onSelect={setActiveLetter}
      />

      <section className="mx-auto w-full max-w-[1400px] px-6 py-10 md:py-12">
        <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-foreground-600">
            {loading ? (
              "Loading featured listings..."
            ) : (
              <>
                Showing{" "}
                <span className="font-semibold text-foreground-900">{filtered.length}</span>
                {activeLetter ? (
                  <>
                    {" "}
                    listing{filtered.length === 1 ? "" : "s"} starting with{" "}
                    <span className="font-semibold text-primary-700">{activeLetter}</span>
                  </>
                ) : (
                  <> featured listing{filtered.length === 1 ? "" : "s"}</>
                )}
              </>
            )}
          </p>
          {activeLetter ? (
            <button
              type="button"
              onClick={() => setActiveLetter(null)}
              className="inline-flex cursor-pointer items-center gap-1.5 self-start whitespace-nowrap rounded-md border border-background-300 px-3.5 py-2 text-xs font-semibold text-foreground-700 transition-colors duration-200 hover:border-primary-300 hover:text-primary-700"
            >
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                <i className="ri-refresh-line text-sm" />
              </span>
              Show all letters
            </button>
          ) : null}
        </div>

        {error ? (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-error-warning-line text-2xl" />
            </span>
            <h3 className="mt-4 font-heading text-lg font-bold text-foreground-900">
              Couldn&apos;t load featured listings
            </h3>
            <p className="mt-2 max-w-md text-sm text-foreground-600">
              Something went wrong while fetching the listings. Please try again.
            </p>
            <button
              type="button"
              onClick={() => setActiveLetter(null)}
              className="mt-5 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-refresh-line text-base" />
              </span>
              Retry
            </button>
          </div>
        ) : (
          <BusinessGrid
            businesses={filtered}
            loading={loading}
            emptyTitle="No featured listings here yet"
            emptyMessage="There are no featured businesses starting with this letter right now. Try another letter or view all listings."
          />
        )}
      </section>

      <section className="border-t border-background-200 bg-background-100">
        <div className="mx-auto flex w-full max-w-[1400px] flex-col items-start gap-5 px-6 py-10 md:flex-row md:items-center md:justify-between md:py-12">
          <div>
            <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
              Want your business featured here?
            </h2>
            <p className="mt-1.5 max-w-xl text-sm text-foreground-600">
              Upgrade to a featured plan to appear at the top of search, on this page and across the
              coast — reaching thousands of local customers every month.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/directory"
              className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm font-semibold text-foreground-700 transition-colors duration-200 hover:bg-background-50"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-search-line text-base" />
              </span>
              Browse all businesses
            </Link>
            <Link
              to="/listing-plans"
              className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2.5 text-sm font-semibold text-foreground-950 transition-colors duration-200 hover:bg-accent-600"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-vip-crown-line text-base" />
              </span>
              View listing plans
            </Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}